import { useState, type ReactElement } from "react";
import { ScanBarcode } from "lucide-react";
import { IconButton } from "@ux/primitives";
import { BarcodeScannerSheet } from "./BarcodeScannerSheet";
import {
    isBarcodeScannerSupported,
    type BarcodeScanResult,
} from "./useBarcodeScanner";
import type { BarcodeFormat } from "./barcode-detector";

export interface ScanBarcodeButtonProps {
    /** Called with the decoded barcode value (and detected format) on a successful scan. */
    readonly onScan: (result: BarcodeScanResult) => void;
    /** Sheet heading; defaults to "Scan barcode". Override per field for clarity ("Scan asset tag", "Scan serial number"). */
    readonly title?: string;
    /** Sheet sub-heading; helps users with low-contrast labels know what they're aiming at. */
    readonly description?: string;
    /** Optional format scoping; defaults to the hook's default set. */
    readonly formats?: readonly BarcodeFormat[];
    /** Disabled state — typically follows the parent input's disabled state. */
    readonly disabled?: boolean;
    /** ARIA label for the icon button; defaults to "Scan barcode". */
    readonly ariaLabel?: string;
}

/**
 * Trailing-icon button that opens the live barcode scanner. Designed to
 * sit in the same flex row as a `<TextInput>` so users see "type or scan"
 * as one unified affordance.
 *
 * Renders nothing when the browser doesn't expose `BarcodeDetector` —
 * graceful degradation rather than a broken control. The user can always
 * still type the value into the input.
 *
 * Field-agnostic: the parent decides which input to fill and how to label
 * the sheet. Adding a scan button to a new field is one JSX line; see
 * AssetForm's asset-tag row for the canonical wiring.
 */
export function ScanBarcodeButton({
    onScan,
    title,
    description,
    formats,
    disabled = false,
    ariaLabel = "Scan barcode",
}: ScanBarcodeButtonProps): ReactElement | null {
    const [open, setOpen] = useState(false);

    if (!isBarcodeScannerSupported()) {
        return null;
    }

    return (
        <>
            <IconButton
                icon={<ScanBarcode size={16} />}
                label={ariaLabel}
                onClick={() => setOpen(true)}
                disabled={disabled}
            />
            <BarcodeScannerSheet
                open={open}
                {...(title !== undefined ? { title } : {})}
                {...(description !== undefined ? { description } : {})}
                {...(formats !== undefined ? { formats } : {})}
                onResult={(result) => {
                    setOpen(false);
                    onScan(result);
                }}
                onClose={() => setOpen(false)}
            />
        </>
    );
}
